import { ReactNode } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import URLS from 'URLS';
import { useUserState } from 'hooks/User';
import { useTranslation } from 'react-i18next';

// Material UI Components
import Box from '@mui/material/Box';
import { FlexRowBox } from 'components/layout/FlexBox';
import { DesktopOnly, MobileOnly } from 'components/layout/ResponsiveBox';
import Button from '@mui/material/Button';
import { NavIconButton } from 'components/navigation/NavIconButton';
import { TopBarGrowBox } from 'components/navigation/TopBarNav';
import PersonIcon from 'components/navigation/PersonIcon';

// Assets/Icons
import LOGO from 'assets/icons/ECPlayLogo.svg';
import AccountIcon from '@mui/icons-material/AccountCircleOutlined';
import EventIcon from '@mui/icons-material/Event';
import ChallengeIcon from '@mui/icons-material/EmojiEventsRounded';
import PlayerIcon from '@mui/icons-material/PlayArrowRounded';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AcademyIcon from '@mui/icons-material/School';
import ResourcesIcon from '@mui/icons-material/LibraryBooks';

// Project components
import Paper from 'components/layout/Paper';
import Sidebar from 'components/navigation/Sidebar';
import Apps from 'components/navigation/Apps';
import Notifications from 'components/navigation/Notifications';
import { useAuth, useIsSchoolAuth } from 'hooks/Auth';
import { useAdmin } from 'apps/AdminPanel/hooks/useAdmin';

type URIbuttonProps = {
  data: {
    link: string;
    icon: ReactNode;
    text: string;
    external?: boolean;
  };
};

const URIbutton = ({ data }: URIbuttonProps) => {
  const navigate = useNavigate();
  return (
    <Button
      color='secondary'
      onClick={
        data.link === window.location.pathname
          ? () => window.location.reload()
          : () => {
              if (data.external) {
                window.open(data.link);
              } else {
                navigate(data.link);
              }
            }
      }
      startIcon={data.icon}
      sx={{ mx: '5px' }}
      variant={window.location.pathname === data.link ? 'outlined' : 'text'}>
      {data.text}
    </Button>
  );
};

function TopBar() {
  const { t } = useTranslation(['common']);
  const user = useUserState();
  const [auth, isAuthLoading] = useAuth();
  const [admin, isAdminLoading] = useAdmin();
  const [isSchoolAuth, _, schoolAuthDoc] = useIsSchoolAuth();
  const isSchoolStudent = schoolAuthDoc !== null ? schoolAuthDoc?.data().role === 'student' : false;
  const location = useLocation();

  return (
    <Paper
      outlined
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        py: 1,
        px: 0,
        margin: 'auto',
        width: '100%',
        mb: 0,
      })}>
      <Box
        sx={(theme) => ({
          maxWidth: theme.breakpoints.values.xl,
          margin: 'auto',
          px: 1,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
        })}>
        <MobileOnly>
          <Sidebar />
        </MobileOnly>
        <Button
          component={Link}
          sx={(theme) => ({
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            mr: '10px',
            [theme.breakpoints.down('lg')]: {
              pl: '15px',
            },
          })}
          to={isSchoolStudent ? URLS.HUB : location.pathname === URLS.HUB ? URLS.LANDING : URLS.HUB}>
          <img alt='EC-Play logo' height='40em' src={LOGO} width='auto' />
        </Button>
        <TopBarGrowBox>
          <Box sx={{ width: '100%', display: { xs: 'none', lg: 'block' } }}>
            <FlexRowBox>
              {import.meta.env.VITE_SHOW_CHALLENGE === 'TRUE' && isSchoolAuth !== true && (
                <URIbutton data={{ link: URLS.CHALLENGE, text: t('APPS.CHALLENGE'), icon: <ChallengeIcon /> }} />
              )}
              {import.meta.env.VITE_SHOW_EVENTS === 'TRUE' && isSchoolAuth !== true && (
                <URIbutton data={{ link: URLS.EVENTS_EVENTS, text: t('APPS.EVENTS'), icon: <EventIcon /> }} />
              )}
              {import.meta.env.VITE_SHOW_PLAYER === 'TRUE' && <URIbutton data={{ link: URLS.EDTECH, text: t('APPS.EDTECH'), icon: <PlayerIcon /> }} />}
              {import.meta.env.VITE_SHOW_RESOURCES === 'TRUE' && (
                <URIbutton data={{ link: URLS.RESOURCES, text: t('APPS.RESOURCES'), icon: <ResourcesIcon /> }} />
              )}
              {import.meta.env.VITE_SHOW_ACADEMY === 'TRUE' && <URIbutton data={{ link: URLS.ACADEMY, text: t('APPS.ACADEMY'), icon: <AcademyIcon /> }} />}
              {isSchoolStudent !== true && <URIbutton data={{ link: URLS.SHOP, text: t('APPS.SHOP'), icon: <ShoppingCartIcon />, external: true }} />}

              {/*
              This is temporary removed because there is no "about us" page.
              <URIbutton data={{ link: URLS.ORG, text: t('APPS.ORG'), icon: <InfoIcon />, external: true }} />
              */}
            </FlexRowBox>
          </Box>
        </TopBarGrowBox>
        <Notifications />
        <DesktopOnly>
          <Apps />
        </DesktopOnly>
        <div>
          {user ? (
            <PersonIcon img={user.data()?.picture} name={user.data()?.name || ''} />
          ) : (
            <NavIconButton component={Link} state={{ referrer: window.location.pathname }} to={URLS.SIGN_IN}>
              <AccountIcon />
            </NavIconButton>
          )}
        </div>
      </Box>
    </Paper>
  );
}

export default TopBar;
